<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      {
        expanded:
          !isVerticalMenuCollapsed ||
          (isVerticalMenuCollapsed && isMouseHovered)
      },
      (skin === 'semi-dark' || skin === 'dark') ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row justify-content-center">
          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link
              class="navbar-brand"
              to="/dashboard"
            >
              <span class="brand-logo">
                <b-img
                  :src="appLogoImage"
                  alt="logo"
                />
              </span>
              <h2
                class="brand-text ml-2 mb-0"
                style="color: #1877f2;"
              >
                {{ appName }}
              </h2>
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- Shadow -->
    <div
      :class="{ 'd-block': shallShadowBottom }"
      class="shadow-bottom"
    />
    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="
        evt => {
          shallShadowBottom = evt.srcElement.scrollTop > 0;
        }
      "
    >
      <vertical-nav-menu-items
        ref="vertical-menu"
        :items="filteredNavMenuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>

    <!-- /main menu content-->

    <!-- main menu footer -->
    <div class="vertical-menu-footer d-flex align-items-center justify-content-between">
      <div class="vertical-menu-footer__actions d-flex align-items-center">
        <b-dropdown
          right
          dropup
          no-caret
          variant="link"
          toggle-class="vertical-footer-user-toggle d-flex align-items-center p-0"
          class="vertical-footer-user-dropdown"
        >
          <template #button-content>
            <b-avatar
              size="38"
              variant="light-primary"
              class="vertical-footer-user-avatar"
            >
              <feather-icon
                icon="UserIcon"
                size="20"
              />
            </b-avatar>
          </template>

          <b-dropdown-item
            :to="{ name: 'user-profile' }"
            link-class="d-flex align-items-center"
          >
            <feather-icon
              size="16"
              icon="UserIcon"
              class="mr-50"
            />
            <span>Profile</span>
          </b-dropdown-item>

          <b-dropdown-item
            link-class="d-flex align-items-center"
            @click="logout"
          >
            <feather-icon
              size="16"
              icon="LogOutIcon"
              class="mr-50"
            />
            <span>Logout</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>

      <div
        v-if="!isVerticalMenuCollapsed || isMouseHovered"
        class="vertical-menu-footer__theme"
      >
        <dark-toggler />
      </div>
    </div>
    <!-- /main menu footer -->
  </div>
</template>

<script>
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import {
  BLink,
  BImg,
  BDropdown,
  BDropdownItem,
  BAvatar,
} from 'bootstrap-vue'
import { provide, computed, ref } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
// import { mapState } from 'vuex'
import DarkToggler from '@core/layouts/components/app-navbar/components/DarkToggler.vue'
import navMenuItems from '@/navigation/vertical'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'
// import LogoName from './LogoName.vue'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
    BDropdown,
    BDropdownItem,
    BAvatar,
    DarkToggler,
    // LogoName,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))
    // App Name
    const { appName, appLogoImage } = $themeConfig.app
    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
    }
  },
  data() {
    return {
      userRole: '',
      halalTemplateSetup: false,
      permissions: [],
      // Comma-separated values from localStorage "sources", e.g. "sfda,qfda"
      sources: [],
    }
  },

  computed: {
    allowedRoles() {
      const roles = [this.userRole, ...this.permissions]
      if (this.halalTemplateSetup) {
        roles.push('halal-template')
      }
      return new Set(roles.filter(Boolean))
    },
    filteredNavMenuItems() {
      const allowedSet = this.allowedRoles
      // Requirement: for `engineer-allocation`, show SFDA tabs only if `localStorage.sources`
      // includes `sfda` or `sfda-eg`.
      const isEngineerAllocation = allowedSet.has('engineer-allocation')
      const hasRequiredSfdaSource = this.sources.includes('sfda') || this.sources.includes('sfda-eg')
      const shouldShowSfdaTabs = !isEngineerAllocation || hasRequiredSfdaSource

      const filterChildren = children => children
        .map(child => {
          if (
            !shouldShowSfdaTabs
            && (child.route === 'sfda-labs' || child.route === 'importer')
          ) {
            return null
          }

          if (child.children?.length) {
            const nestedChildren = filterChildren(child.children)
            if (nestedChildren.length > 0) {
              return { ...child, children: nestedChildren }
            }
            return null
          }
          return child.role?.some(role => allowedSet.has(role)) ? child : null
        })
        .filter(child => child !== null)

      return this.navMenuItems
        .map(item => {
          if (item.children?.length) {
            const filteredChildren = filterChildren(item.children)
            if (filteredChildren.length > 0) {
              return { ...item, children: filteredChildren }
            }
            return null
          }

          if (!shouldShowSfdaTabs && (item.route === 'sfda-labs' || item.route === 'importer')) {
            return null
          }

          return item.role?.some(role => allowedSet.has(role))
            ? item
            : null
        })
        .filter(item => item !== null)
    },
  },
  mounted() {
    // Safely read role and permissions from localStorage; handle missing values
    this.userRole = localStorage.getItem('accessRole') || ''
    this.halalTemplateSetup = localStorage.getItem('halal_template_setup') === 'true'
    const rawPermissions = localStorage.getItem('permissions')
    this.permissions = rawPermissions
      ? String(rawPermissions).split(',')
      : []

    const rawSources = localStorage.getItem('sources')
    this.sources = rawSources
      ? String(rawSources).split(',').map(s => s.trim()).filter(Boolean)
      : []
  },
  methods: {
    logout() {
      // Remove stored tokens
      localStorage.removeItem('access_token')
      localStorage.removeItem('accessToken')

      // Notify other tabs about logout
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        const authChannel = new BroadcastChannel('auth-sync')
        authChannel.postMessage({ type: 'logout' })
        authChannel.close()
      }

      // Redirect to login route (same behavior as header navbar)
      this.$router.push({ name: 'login' })
    },
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
</style>
